/**
 * Finding Your Voice — Guide Characters
 *
 * Three Greek guides who accompany learners through the programme.
 * Each character represents a different archetype of communication mastery.
 */

export const CHARACTERS = [
  {
    id: 'philemon',
    name: 'Philemon',
    greekName: 'Φιλήμων',
    meaning: 'Kindly, affectionate',
    archetype: 'Inner Work',
    domain: 'Fear, emotion, identity',
    voice: 'Warm, reflective, self-deprecating',
    iconName: 'Heart',
    colour: '#6B7D3E',
    colourName: 'olive',
    primaryModules: [1, 2, 5],
    supportingModules: [4, 8],
    quote: 'The trembling never fully leaves. But it no longer speaks for me.',
    bio: `Philemon arrived at Aristotle's Lyceum with a mind full of ideas and a voice that refused to share them. He spent his first year watching others speak while dreading his own turn, memorising arguments he never delivered, rehearsing phrases that dissolved the moment anyone looked his way.

What changed things was understanding why he was afraid, more than any technique or practice drill. He came to see the voice in his chest as a signal worth listening to, and stopped reading it as weakness.

Now in his middle years, Philemon teaches young orators at the Lyceum. He is patient, unhurried, and honest about his own ongoing struggles. He never forgot what it felt like to stand at the edge of a conversation, desperate to contribute but paralysed by something he couldn't name.

He believes that before you can speak well, you must first make peace with the part of you that is afraid to speak at all.`,
    speechPatterns: [
      'Reflective and measured pace',
      'Often begins with "I remember when..."',
      'Gentle humour at his own expense',
      'Occasional pauses mid-thought, comfortable with silence',
      'Uses personal stories to normalise struggle',
      'Frames fear as information rather than failure',
    ],
    gift: 'The ability to make people feel safe enough to be honest about what frightens them.',
    personality: {
      strengths: [
        'Deep empathy and emotional attunement',
        'Patience with slow progress',
        'Ability to normalise vulnerability',
        'Remembers his own struggles vividly',
      ],
      limitations: [
        'Can be overly cautious, and sometimes holds learners back from leaping',
        'Occasionally gets lost in reflection when action is needed',
        'Still fights his own nerves before large gatherings',
      ],
      teachingStyle: 'Socratic and gentle: draws insights out rather than pushing them in. Favours journalling, self-reflection exercises, and gradual exposure. Will sit with discomfort rather than rushing past it.',
    },
  },
  {
    id: 'xenia',
    name: 'Xenia',
    greekName: 'Ξενία',
    meaning: 'Hospitality, guest-friendship',
    archetype: 'Relational Work',
    domain: 'Listening, connection, audience',
    voice: 'Curious, wise, gently challenging',
    iconName: 'Users',
    colour: '#3D6B7E',
    colourName: 'aegean',
    primaryModules: [4, 8],
    supportingModules: [1, 2, 5],
    quote: 'The most powerful thing you can do in a room is make space for someone else\'s voice.',
    bio: `Xenia was born in Miletus to a family of textile merchants who traded across the Aegean. She married young, was widowed soon after, and came to Athens with little more than her mother's loom and an uncommon talent for making strangers feel welcome.

She created her own gathering space because the rooms where real conversations happened were built for men. The only women at a symposium were hired companions and entertainers, the Assembly was open to male citizens alone, and the philosophical schools took a woman only rarely. So she built her own. Her courtyard now draws philosophers, poets, merchants, foreigners, and anyone with something worth saying and the courage to say it honestly.

Xenia's genius is for listening. She hears what people mean beneath what they say. She notices who hasn't spoken yet. She asks the question that turns the conversation.

She believes that communication is less about projecting your voice outward than about creating the conditions where a real exchange becomes possible.`,
    speechPatterns: [
      'Asks questions as often as she makes statements',
      'Frequently uses "What do you notice when..."',
      'Tells stories about her guests to illustrate points',
      'Offers quiet challenges: never aggressive, always precise',
      'Comfortable holding tension in a conversation',
      'Uses hospitality metaphors (welcoming, making room, setting the table)',
    ],
    gift: 'The ability to make any room feel like a place where honesty is both safe and expected.',
    personality: {
      strengths: [
        'Extraordinary listening and observation skills',
        'Creates psychological safety effortlessly',
        'Sees group dynamics others miss',
        'Connects people who need to hear each other',
      ],
      limitations: [
        'Can prioritise harmony over necessary confrontation',
        'Sometimes forgets that not every conversation needs facilitation',
        'Her own story often stays hidden behind her hosting role',
      ],
      teachingStyle: 'Experiential and relational, favouring pair work, group exercises, role play, and live observation. Teaches listening as a primary skill. Will redirect a learner\'s attention from themselves to their audience.',
    },
  },
  {
    id: 'theron',
    name: 'Theron',
    greekName: 'Θήρων',
    meaning: 'Hunter',
    archetype: 'Practical Work',
    domain: 'Clarity, structure, body',
    voice: 'Direct, energetic, grounded',
    iconName: 'Compass',
    colour: '#C2703E',
    colourName: 'terracotta',
    primaryModules: [3, 6, 7],
    supportingModules: [4, 5],
    quote: "I've learned more about persuasion from a fishwife in Syracuse than most scholars learn in a lifetime.",
    bio: `Theron traded wine, olive oil, and pottery across the Mediterranean for thirty years. He's haggled in Phoenician ports, argued with Carthaginian customs officials, told stories to keep sailors calm during storms, and convinced sceptical merchants in a dozen languages that his goods were worth the price.

He never studied at the Lyceum. He never read Aristotle's Rhetoric. He discovered the art of persuasion backwards: he noticed what worked in the real world, then later found out the philosophers had names for all of it. Ethos, pathos and logos (the speaker's character, the listener's feelings and the argument itself) were things Theron had been using his whole life without knowing the labels.

Now semi-retired from trade, he spends his evenings at Xenia's gatherings, where he is equal parts beloved storyteller and affectionate provocateur. He has no patience for theory divorced from practice, and he believes the body knows things the mind hasn't caught up with yet.

He is the one who will make you stand up and try it. Right now. No more preparing.`,
    speechPatterns: [
      'Concrete and grounded; always ties ideas to real experience',
      'Often begins with "Let me tell you what I saw..."',
      'Rich in trade metaphors: cargoes, markets, currents, harbours',
      'Affectionate exasperation when learners overthink',
      'Energetic and physical (gestures widely, moves around)',
      'Blunt but never cruel, his directness delivered with warmth',
    ],
    gift: 'The ability to cut through overthinking and get people into action before their doubts catch up.',
    personality: {
      strengths: [
        'Vast real-world experience across cultures',
        'Makes abstract concepts tangible and memorable',
        'Infectious energy and confidence',
        'Sees through excuses with good humour',
      ],
      limitations: [
        'Can underestimate how paralysing fear is for some people',
        'Sometimes dismisses theory that would actually help',
        'His "just do it" approach doesn\'t suit every learner',
      ],
      teachingStyle: 'Experiential and physical: favours body work, vocal exercises, improvisational drills, and real-world practice. Will push learners out of comfort zones. Believes in learning by doing and reflecting after.',
    },
  },
];

/**
 * How the three characters relate to each other and complement
 * each other's approaches within the programme.
 */
export const CHARACTER_DYNAMIC = {
  overview: `Philemon, Xenia, and Theron represent three dimensions of finding your voice: the inner work (facing your fears and understanding yourself), the relational work (connecting with others and reading rooms), and the practical work (structuring your message and using your body). No single guide is enough; you need all three.`,
  relationships: [
    {
      pair: ['philemon', 'theron'],
      dynamic: 'Respectful tension',
      description: `Philemon and Theron approach communication from opposite ends. Philemon wants to understand the fear before acting; Theron wants to act despite the fear. They argue affectionately about this at Xenia's gatherings. Each privately admits the other has a point. Learners benefit from both perspectives: sometimes you need to sit with discomfort, sometimes you need to leap.`,
    },
    {
      pair: ['philemon', 'xenia'],
      dynamic: 'Quiet kinship',
      description: `Philemon and Xenia share a deep understanding of what it means to feel unseen. Philemon was silenced by his own fear; Xenia was silenced by a society that didn't make room for her voice. They approach healing differently (Philemon turns inward, Xenia turns outward), but they recognise the same wound in each other.`,
    },
    {
      pair: ['xenia', 'theron'],
      dynamic: 'Complementary energy',
      description: `Xenia and Theron are the most natural collaborators. She creates the space; he fills it with energy. She listens; he speaks. She notices who's been left out; he draws them in with a story or a challenge. Theron is one of the few people who can make Xenia laugh so hard she forgets to facilitate.`,
    },
  ],
  sharedPrinciple: 'All three guides believe the same thing: your voice already exists. The work is removing what\'s in the way, not creating something new.',
};

/**
 * Maps each module to its primary guide and any supporting guides.
 * Kept in step with MODULE_METADATA in data/modules/index.js.
 *
 * Module 1: Why Speaking Feels Scary — Philemon (primary), Xenia + Theron (supporting)
 * Module 2: Who Are You When You Speak? — Philemon (primary), Xenia (supporting)
 * Module 3: What Do You Actually Want to Say? — Theron (primary), Philemon + Xenia (supporting)
 * Module 4: The Listener's Mindset — Xenia (primary), Theron + Philemon (supporting)
 * Module 5: Emotion Is Information — Philemon (primary), Xenia + Theron (supporting)
 * Module 6: Your Body Already Knows — Theron (primary), Philemon + Xenia (supporting)
 * Module 7: Structure Sets You Free — Theron (primary), Philemon (supporting)
 * Module 8: The Courage to Be Heard — all three in dialogue (Xenia hosts the farewell,
 *           so she renders as primary; the other two are always shown alongside her)
 */
export const MODULE_CHARACTER_MAP = {
  1: { primary: 'philemon', supporting: ['xenia', 'theron'] },
  2: { primary: 'philemon', supporting: ['xenia'] },
  3: { primary: 'theron', supporting: ['philemon', 'xenia'] },
  4: { primary: 'xenia', supporting: ['theron', 'philemon'] },
  5: { primary: 'philemon', supporting: ['xenia', 'theron'] },
  6: { primary: 'theron', supporting: ['philemon', 'xenia'] },
  7: { primary: 'theron', supporting: ['philemon', 'xenia'] },
  8: { primary: 'xenia', supporting: ['philemon', 'theron'] },
};

/**
 * Returns a character object by its ID.
 * @param {string} id — The character ID (e.g. 'philemon', 'xenia', 'theron')
 * @returns {object|undefined} The character object, or undefined if not found.
 */
export const getCharacterById = (id) => {
  return CHARACTERS.find((character) => character.id === id);
};

/**
 * Returns the primary and supporting character objects for a given module number.
 * @param {number} moduleNumber — The module number (1–8)
 * @returns {{ primary: object, supporting: object[] }|null} Character objects, or null if module not found.
 */
export const getCharacterForModule = (moduleNumber) => {
  const mapping = MODULE_CHARACTER_MAP[moduleNumber];
  if (!mapping) return null;

  const primary = getCharacterById(mapping.primary);
  const supporting = mapping.supporting.map(getCharacterById).filter(Boolean);

  return { primary, supporting };
};
